/**
 * Capa de DEA de terreno.
 *
 * Cada DEA son seis objetos Leaflet que se mueven juntos:
 *   - `marker`  punto rojo, la ubicación exacta del equipo;
 *   - `circle`  el radio de cobertura;
 *   - `linea`   la cota del radio, del centro al borde;
 *   - `label`   el texto "R 150 m" rotado sobre esa cota;
 *   - `tirador` el mango que gira la cota;
 *   - `icono`   el pictograma AED, que se arrastra por separado y queda unido al
 *               punto por una `guia` punteada. Se separa a propósito: en un plano
 *               apretado el símbolo tiene que poder salirse del círculo sin
 *               mover el equipo.
 */

import L from 'leaflet';
import { bearingBetween, destinationPoint } from '../domain/geo.js';
import {
  DASH_PROPOSED,
  deaColor,
  deaNumberIcon,
  deaPointIcon,
  handleIcon,
  radiusLabelIcon,
} from './icons.js';

export class DeaLayer {
  /**
   * @param {L.Map} map
   * @param {{onChange: () => void, onCommit: () => void}} handlers
   *   `onChange` corre en cada frame de arrastre; `onCommit` sólo al soltar, y es
   *   el que dispara el autoguardado.
   */
  constructor(map, { onChange, onCommit }) {
    this.map = map;
    this.onChange = onChange;
    this.onCommit = onCommit;
    this.items = [];
    this.radius = 150;
    this.nextId = 0;
  }

  get length() {
    return this.items.length;
  }

  /** Datos planos para el dominio y la exportación, sin objetos de Leaflet. */
  snapshot() {
    return this.items.map((dea) => {
      const center = dea.marker.getLatLng();
      const icon = dea.icono.getLatLng();
      return {
        lat: center.lat,
        lng: center.lng,
        estado: dea.estado,
        num: dea.num,
        bearing: dea.bearing,
        icono: [icon.lat, icon.lng],
      };
    });
  }

  /** Centros [lat, lng] para el cálculo de cobertura. */
  centers() {
    return this.items.map((dea) => {
      const center = dea.circle.getLatLng();
      return [center.lat, center.lng];
    });
  }

  setRadius(radius) {
    this.radius = radius;
    this.items.forEach((dea) => {
      dea.circle.setRadius(radius);
      this.#reposition(dea, dea.marker.getLatLng());
    });
  }

  add(lat, lng, estado, bearing = 0, num = null, iconLatLng = null) {
    const number = num == null ? this.items.length + 1 : num;
    const color = deaColor(number);
    const center = L.latLng(lat, lng);

    const circle = L.circle(center, {
      radius: this.radius,
      color,
      weight: 2.5,
      fillColor: color,
      fillOpacity: 0.1,
      dashArray: estado === 'propuesto' ? DASH_PROPOSED : null,
    }).addTo(this.map);

    const edge = destinationPoint(lat, lng, this.radius, bearing);
    const linea = L.polyline([[lat, lng], edge], { color: '#ff2a2a', weight: 2 }).addTo(this.map);

    const mid = destinationPoint(lat, lng, this.radius / 2, bearing);
    const label = L.marker(mid, {
      icon: radiusLabelIcon(`R ${this.radius} m`, bearing),
      interactive: false,
    }).addTo(this.map);

    const tirador = L.marker(edge, { draggable: true, icon: handleIcon() }).addTo(this.map);
    tirador.bindTooltip('Arrastra para girar', { direction: 'top' });

    const marker = L.marker(center, {
      draggable: true,
      zIndexOffset: 700,
      icon: deaPointIcon(),
    }).addTo(this.map);
    marker.bindTooltip('Ubicación del DEA (mueve el círculo)', { direction: 'top' });

    // El pictograma nace desplazado 26 m al sur para no tapar el punto.
    const iconPosition = iconLatLng
      ? L.latLng(iconLatLng[0], iconLatLng[1])
      : L.latLng(...destinationPoint(lat, lng, 26, 180));
    const icono = L.marker(iconPosition, {
      draggable: true,
      zIndexOffset: 720,
      icon: deaNumberIcon(number),
    }).addTo(this.map);
    icono.bindTooltip('Mueve solo el equipo', { direction: 'top' });

    const guia = L.polyline([center, iconPosition], {
      color: '#fff',
      weight: 1.5,
      dashArray: '4 3',
    }).addTo(this.map);

    const dea = {
      id: ++this.nextId,
      marker,
      icono,
      guia,
      circle,
      linea,
      label,
      tirador,
      estado,
      num: number,
      bearing,
      color,
    };

    let offsetLat = iconPosition.lat - lat;
    let offsetLng = iconPosition.lng - lng;

    marker.on('dragstart', () => {
      const point = marker.getLatLng();
      const iconAt = icono.getLatLng();
      offsetLat = iconAt.lat - point.lat;
      offsetLng = iconAt.lng - point.lng;
    });
    marker.on('drag', (event) => {
      const point = event.latlng;
      this.#reposition(dea, point);
      const iconTarget = L.latLng(point.lat + offsetLat, point.lng + offsetLng);
      icono.setLatLng(iconTarget);
      guia.setLatLngs([point, iconTarget]);
    });
    marker.on('dragend', () => this.onCommit());

    icono.on('drag', (event) => guia.setLatLngs([marker.getLatLng(), event.latlng]));
    icono.on('dragend', () => this.onCommit());

    tirador.on('drag', (event) => {
      const center2 = dea.marker.getLatLng();
      dea.bearing = bearingBetween(center2.lat, center2.lng, event.latlng.lat, event.latlng.lng);
      this.#reposition(dea, center2);
    });
    tirador.on('dragend', () => this.onCommit());

    this.items.push(dea);
    this.renumber();
    this.onChange();
    return dea;
  }

  /** Recoloca círculo, cota, etiqueta y tirador de un DEA sobre una nueva posición. */
  #reposition(dea, latLng) {
    dea.circle.setLatLng(latLng);
    const edge = destinationPoint(latLng.lat, latLng.lng, this.radius, dea.bearing);
    dea.linea.setLatLngs([[latLng.lat, latLng.lng], edge]);
    dea.tirador.setLatLng(edge);
    const mid = destinationPoint(latLng.lat, latLng.lng, this.radius / 2, dea.bearing);
    dea.label.setLatLng(mid);
    dea.label.setIcon(radiusLabelIcon(`R ${this.radius} m`, dea.bearing));
  }

  remove(id) {
    const index = this.items.findIndex((dea) => dea.id === id);
    if (index < 0) return;
    this.#destroy(this.items[index]);
    this.items.splice(index, 1);
    this.renumber();
    this.onChange();
  }

  clear() {
    this.items.forEach((dea) => this.#destroy(dea));
    this.items.length = 0;
    this.nextId = 0;
  }

  #destroy(dea) {
    [dea.marker, dea.icono, dea.guia, dea.circle, dea.linea, dea.label, dea.tirador].forEach(
      (layer) => {
        if (layer) this.map.removeLayer(layer);
      },
    );
  }

  /** Renumera 1..N para que borrar uno del medio no deje huecos. */
  renumber() {
    this.items.forEach((dea, index) => {
      dea.num = index + 1;
      dea.color = deaColor(dea.num);
      dea.circle.setStyle({ color: dea.color, fillColor: dea.color });
      if (dea.icono?.setIcon) dea.icono.setIcon(deaNumberIcon(dea.num));
    });
  }
}
